import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";

export const Route = createFileRoute("/_authed/prompts/$id/")({
  component: PromptDetailPage,
});

function PromptDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editingMeta, setEditingMeta] = useState(false);
  const [confirmDeleteVersion, setConfirmDeleteVersion] = useState<string | null>(
    null,
  );

  const promptQ = useQuery({
    queryKey: ["admin", "prompt", id],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/prompts/{prompt_id}", {
        params: { path: { prompt_id: id } },
      });
      if (error) throw new Error("not found");
      return data!;
    },
  });

  const versionsQ = useQuery({
    queryKey: ["admin", "prompt", id, "versions"],
    queryFn: async () => {
      const { data, error } = await api.GET(
        "/api/v1/admin/prompts/{prompt_id}/versions",
        { params: { path: { prompt_id: id } } },
      );
      if (error) throw new Error("failed");
      return data!;
    },
  });

  const newBlankVersion = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST(
        "/api/v1/admin/prompts/{prompt_id}/versions",
        {
          params: { path: { prompt_id: id } },
          body: {},
        },
      );
      if (error) throw new Error("create version failed");
      return data!;
    },
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id, "versions"] });
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id] });
      navigate({ to: "/prompts/$id/versions/$vid", params: { id, vid: v.id } });
    },
  });

  const newVersionFromActive = useMutation({
    mutationFn: async () => {
      if (!promptQ.data?.active_version) throw new Error("no active version");
      const { data, error } = await api.POST(
        "/api/v1/admin/prompts/{prompt_id}/versions",
        {
          params: { path: { prompt_id: id } },
          body: { from_version_id: promptQ.data.active_version.id },
        },
      );
      if (error) throw new Error("create version failed");
      return data!;
    },
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id, "versions"] });
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id] });
      navigate({ to: "/prompts/$id/versions/$vid", params: { id, vid: v.id } });
    },
  });

  const deleteVersion = useMutation({
    mutationFn: async (vid: string) => {
      const { error } = await api.DELETE(
        "/api/v1/admin/prompts/{prompt_id}/versions/{version_id}",
        { params: { path: { prompt_id: id, version_id: vid } } },
      );
      if (error) throw new Error("delete failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id, "versions"] });
      setConfirmDeleteVersion(null);
    },
  });

  if (promptQ.isPending)
    return <p className="text-sm text-zinc-500">Loading…</p>;
  if (promptQ.isError || !promptQ.data)
    return <p className="text-sm text-red-600">Failed to load prompt.</p>;
  const prompt = promptQ.data;

  return (
    <div className="space-y-4">
      <Link
        to="/prompts"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
      >
        <ChevronLeft className="size-4" />
        All prompts
      </Link>

      <Card>
        <CardHeader>
          <div>
            <div className="font-mono text-xs text-zinc-500">{prompt.code}</div>
            <CardTitle>{prompt.name}</CardTitle>
            {prompt.description && (
              <p className="text-sm text-zinc-600">{prompt.description}</p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setEditingMeta(true)}>
            <Pencil className="mr-1 size-4" />
            Edit
          </Button>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Versions</CardTitle>
          <div className="flex gap-2">
            {prompt.active_version && (
              <Button
                size="sm"
                onClick={() => newVersionFromActive.mutate()}
                disabled={newVersionFromActive.isPending}
              >
                <Plus className="mr-1 size-4" />
                New version (from active)
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => newBlankVersion.mutate()}
              disabled={newBlankVersion.isPending}
            >
              <Plus className="mr-1 size-4" />
              New blank version
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {versionsQ.isPending ? (
            <p className="p-4 text-sm text-zinc-500">Loading…</p>
          ) : versionsQ.isError ? (
            <p className="p-4 text-sm text-red-600">Failed to load versions.</p>
          ) : versionsQ.data?.items.length === 0 ? (
            <p className="p-6 text-center text-sm text-zinc-500">
              No versions.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Version</TH>
                  <TH>Status</TH>
                  <TH>LLM</TH>
                  <TH>Template</TH>
                  <TH>Activated</TH>
                  <TH className="w-px text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {versionsQ.data!.items.map((v) => (
                  <TR key={v.id}>
                    <TD className="font-mono text-xs">
                      <Link
                        to="/prompts/$id/versions/$vid"
                        params={{ id, vid: v.id }}
                        className="text-zinc-900 underline"
                      >
                        v{v.version_number}
                      </Link>
                    </TD>
                    <TD>
                      <StatusBadge status={v.status} />
                    </TD>
                    <TD className="text-xs text-zinc-600">
                      {v.llm_code ?? "—"}
                    </TD>
                    <TD className="max-w-md truncate text-xs text-zinc-600">
                      {(v.template ?? "").slice(0, 60) || (
                        <span className="italic text-zinc-400">empty</span>
                      )}
                    </TD>
                    <TD className="text-xs text-zinc-500">
                      {v.activated_at
                        ? new Date(v.activated_at).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : "—"}
                    </TD>
                    <TD className="text-right">
                      {v.status === "draft" && confirmDeleteVersion !== v.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={deleteVersion.isPending}
                          onClick={() => { deleteVersion.reset(); setConfirmDeleteVersion(v.id); }}
                          aria-label="Delete draft"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                      {v.status === "draft" && confirmDeleteVersion === v.id && (
                        <div className="min-w-64 space-y-2 rounded-md border border-red-200 bg-red-50 p-3 text-left" role="group" aria-label={`Delete draft v${v.version_number}`}>
                          <p className="text-sm text-zinc-700">Delete draft v{v.version_number}? Activated and historical versions are kept.</p>
                          {deleteVersion.isError && <p role="alert" className="text-sm text-red-700">Could not delete this draft. Try again.</p>}
                          <div className="flex gap-2">
                            <Button variant="ghost" onClick={() => setConfirmDeleteVersion(null)} disabled={deleteVersion.isPending}>Cancel</Button>
                            <Button variant="danger" onClick={() => deleteVersion.mutate(v.id)} disabled={deleteVersion.isPending}>
                              {deleteVersion.isPending ? "Deleting…" : "Delete"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {editingMeta && (
        <EditMetadataDialog
          prompt={prompt}
          onClose={() => setEditingMeta(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "prompt", id] });
            qc.invalidateQueries({ queryKey: ["admin", "prompts"] });
            setEditingMeta(false);
          }}
        />
      )}

    </div>
  );
}

function StatusBadge({ status }: { status: "draft" | "active" | "retired" }) {
  if (status === "active")
    return (
      <span className="inline-block rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        Active
      </span>
    );
  if (status === "retired")
    return (
      <span className="inline-block rounded bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700">
        Retired
      </span>
    );
  return (
    <span className="inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
      Draft
    </span>
  );
}

function EditMetadataDialog({
  prompt,
  onClose,
  onSaved,
}: {
  prompt: { id: string; name: string; description: string | null };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(prompt.name);
  const [description, setDescription] = useState(prompt.description ?? "");
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: async () => {
      const { error: apiError } = await api.PATCH(
        "/api/v1/admin/prompts/{prompt_id}",
        {
          params: { path: { prompt_id: prompt.id } },
          body: { name, description: description || null },
        },
      );
      if (apiError) throw new Error("update failed");
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : "update failed"),
  });

  return (
    <Dialog open onClose={onClose} title="Edit prompt metadata">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          update.mutate();
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="m_name">Name</Label>
          <Input
            id="m_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="m_description">Description</Label>
          <Textarea
            id="m_description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
